import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useMemo } from 'react';

import type { Maybe } from '#src/__generated__/globalTypes.ts';
import { PaymentMethodProvider } from '#src/__generated__/globalTypes.ts';
import type { CurrentUserProvider_currentUser } from '#src/contexts/currentUser/__generated__/queries.graphql.ts';
import { useQuery } from '#src/hooks/graphql/useQuery.ts';
import { useMangopayCreditCardsEnabled } from '#src/hooks/useMangopayCreditCardsEnabled.ts';
import { isStripeAccount } from '#src/lib/paymentMethod.ts';

import type {
  PaymentProvider_currentUserQuery,
  PaymentProvider_currentUserQueryVariables,
} from './__generated__/useCurrentUsersPaymentMethods.graphql.ts';

const PAYMENT_PROVIDER_CURRENT_USER_QUERY = gql`
  query PaymentProvider_currentUserQuery($provider: PaymentMethodProvider) {
    currentUser {
      slug
      paymentMethods(provider: $provider) {
        id
        card {
          last4
          brand
          type
        }
      }
    }
  }
` as TypedDocumentNode<
  PaymentProvider_currentUserQuery,
  PaymentProvider_currentUserQueryVariables
>;

type PaymentProvider_currentUserQuery_currentUser_paymentMethods = NonNullable<
  PaymentProvider_currentUserQuery['currentUser']
>['paymentMethods'][number];

export const useCurrentUsersPaymentMethods = (
  currentUser: Maybe<CurrentUserProvider_currentUser> | undefined,
  specifiedProvider?: PaymentMethodProvider
): [
  PaymentProvider_currentUserQuery_currentUser_paymentMethods[] | null,
  boolean,
  () => void,
] => {
  const useMangopayCreditCards = useMangopayCreditCardsEnabled();
  const provider = useMemo(() => {
    // Both Stripe accounts share their customers and payment methods, so keep
    // querying the one we have always queried.
    if (isStripeAccount(specifiedProvider)) return PaymentMethodProvider.STRIPE;
    if (specifiedProvider) return specifiedProvider;
    if (useMangopayCreditCards) return PaymentMethodProvider.MANGOPAY;
    return PaymentMethodProvider.STRIPE;
  }, [useMangopayCreditCards, specifiedProvider]);
  const { data, loading, refetch } = useQuery(
    PAYMENT_PROVIDER_CURRENT_USER_QUERY,
    {
      variables: {
        provider,
      },
      skip: !currentUser,
    }
  );

  return [data?.currentUser?.paymentMethods || null, loading, refetch];
};
